import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function WatchVideosTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/watch-videos").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const refreshSite = () => qc.invalidateQueries({ queryKey: ["site"] });

  const add = async () => {
    if (!url.trim()) return toast.error("Paste a YouTube link first.");
    setBusy(true);
    try {
      await api.post("/admin/watch-videos", { url, title });
      setUrl("");
      setTitle("");
      toast.success("Video added to the website.");
      await load();
      refreshSite();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add the video.");
    } finally {
      setBusy(false);
    }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    try {
      await api.post("/admin/reorder", { collection: "watch_videos", ids: next.map((v) => v.id) });
      refreshSite();
    } catch (err) {
      toast.error("Could not save the new order.");
      load();
    }
  };

  const del = async (v) => {
    if (!window.confirm("Remove this video from the website?")) return;
    try {
      await api.delete(`/admin/watch-videos/${v.id}`);
      load();
      refreshSite();
    } catch (err) {
      toast.error("Could not remove the video.");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8 rounded-3xl border border-line bg-white p-5 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-mute">Add a YouTube video</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-watch-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && add()}
            placeholder="https://www.youtube.com/watch?v=…"
            className="flex-1 border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs"
          />
          <input
            data-testid="admin-watch-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && add()}
            placeholder="Title (optional)"
            className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs sm:w-52"
          />
          <button
            data-testid="admin-watch-add-button"
            onClick={add}
            disabled={busy}
            className="flex items-center justify-center gap-2 bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs disabled:opacity-50"
          >
            <Plus size={14} /> Add
          </button>
        </div>
        <p className="mt-3 text-xs text-subtle">
          Shown in the “See it in action” section right below Products. The first video is featured and autoplays muted when visitors scroll to it; clicking another video plays it with sound.
          Paste any normal YouTube link (watch, youtu.be, shorts or live). The section stays hidden until you add one.
        </p>
      </div>

      <div className="divide-y divide-line border border-line bg-white" data-testid="admin-watch-list">
        {items.map((v, i) => (
          <div key={v.id} className="flex items-center gap-4 p-4" data-testid={`admin-watch-row-${i}`}>
            <span className="w-7 font-mono text-[11px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
            <img src={`https://i.ytimg.com/vi/${v.video_id}/default.jpg`} alt="" className="h-12 w-16 border border-line object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{v.title || `Video ${i + 1}`}</p>
              <p className="truncate font-mono text-[10px] text-subtle">{v.url}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move up"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-mute hover:text-ink disabled:opacity-25" aria-label="Move down"><ArrowDown size={15} /></button>
              <button onClick={() => del(v)} className="p-2 text-mute hover:text-srs" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-8 text-center text-sm text-subtle">No videos yet — the section stays hidden on the website until you add one.</p>}
      </div>
    </div>
  );
}
